import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
// @ts-expect-error
import validateEmail from '@printdeal/validation/dist/schemas/defaults/email';
// @ts-expect-error
import yup from '@printdeal/validation/dist/customMethods/customMethods';
import { Button, Alert, Modal } from '@printdeal/ui-library';
import { PriceAttribute, PriceGroup, SelectorContent } from '@printdeal/product-selector';
import { ErrorManagement } from '@printdeal/error-management/react';
import { dispatchDataLayerEntry } from '../../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import InventoryCheckQuoteForm, { FormValues } from './InventoryCheckQuoteForm';
import { isAuthenticated } from '../../../../../helpers/auth';
import useSystemPages from '../../../../../hooks/useSystemPages';
import { RootState } from '../../../../../store';
import { ServiceFactory } from '../../../../../services/ServiceFactory';

const STATUS = {
  LOADED: 'loaded',
  SUBMITTING: 'submitting',
  POSTED: 'posted',
  ERROR: 'error',
};

interface Props {
  product: SelectorContent['product'];
  attributes: PriceAttribute[];
  group: PriceGroup[];
  quantity: number;
  contentfulId: string;
}

const query = graphql`
  {
    portalConfiguration {
      channel {
        siteUrl
      }
    }
  }
`;

const InventoryCheckQuoteButton = ({
  product, contentfulId, attributes, group, quantity,
}: Props) => {
  const authToken = useSelector<RootState, string | undefined>(({ auth }) => auth.token);
  const [home] = useSystemPages('home');
  const homePath = home.path;
  const intl = useIntl();
  const { portalConfiguration: { channel } } = useStaticQuery(query);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [status, setStatus] = useState(STATUS.LOADED);
  const quoteService = ServiceFactory.createQuotesService();
  // Users who are not logged in or who don't have an account, should add their contact info manually.
  // For logged-in users, it is fetched from their account automatically in the backend.
  const shouldUserAddContactInfo = !isAuthenticated(authToken);
  const quoteFormValidationShape = {
    remarks: yup
      .string()
      .trim()
      .required()
      .min(1),
    ...(shouldUserAddContactInfo && {
      firstName: yup
        .string()
        .trim()
        .required()
        .max(75),
      lastName: yup
        .string()
        .trim()
        .required()
        .max(75),
      email: yup
        .string()
        .trim()
        .max(255)
        .required()
        .email()
        .test((email: string) => validateEmail(email)),
      phone: yup
        .string()
        .trim()
        .required()
        .phoneNumber(intl.locale.split('-')[1]),
    }),
  };

  const quoteFormValidation = yup.object().shape(quoteFormValidationShape);
  const quoteFormFormik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      remarks: intl.formatMessage({
        id: 'product.unavailable.quoteModal.defaultRemark',
        defaultMessage: 'Hi, my configuration appears to be out of stock. Are alternatives available?',
      }),
    },
    validationSchema: quoteFormValidation,
    onSubmit: async (values: FormValues) => {
      setStatus(STATUS.SUBMITTING);
      const {
        firstName, lastName, email, phone, remarks,
      } = values;
      const payload = {
        sku: product.mcpSku,
        variableAttributes: attributes,
        groups: group,
        quantity,
        remarks,
        ...(shouldUserAddContactInfo && {
          firstName, lastName, email, phone,
        }),
      };

      try {
        await quoteService.sendQuoteRequest(payload);
        setStatus(STATUS.POSTED);
      } catch (e) {
        setStatus(STATUS.ERROR);
        ErrorManagement.exception({
          exception: e,
          extra: {
            payload: JSON.stringify({
              ...payload,
              // Strip out sensitive data
              firstName: undefined,
              lastName: undefined,
              email: undefined,
              phone: undefined,
            }),
          },
          tags: {
            fileLocation: 'InventoryCheck',
            featureFunction: 'inventoryCheckQuoteButton',
          },
          fingerprint: ['pdp-inventorycheck-quote-error'],
        });
      }
    },
  });

  const { handleSubmit, isSubmitting, resetForm } = quoteFormFormik;

  const toggleModal = () => {
    if (!isModalOpen) {
      resetForm();
      setStatus(STATUS.LOADED);
    }

    setIsModalOpen(!isModalOpen);

    dispatchDataLayerEntry(
      'selector',
      'click',
      {
        'element-type': 'button',
        'element-content': 'Request quote',
        contentfulId,
      },
    );
  };

  return (
    <>
      <Button onClick={toggleModal} className="pd-mt-3" data-cy="inventoryCheckQuoteButton">
        <FormattedMessage
          id="product.unavailable.quoteButton"
          defaultMessage="Request quote"
        />
      </Button>

      <Modal
        title={intl.formatMessage({ id: 'product.unavailable.quoteModal.header', defaultMessage: 'Request quote' })}
        isOpen={isModalOpen}
        onClose={toggleModal}
        dismissLabel={intl.formatMessage({ id: 'general.alert.dismiss', defaultMessage: 'Dismiss' })}
      >
        <Modal.Content>
          {status === STATUS.ERROR && (
          <Alert className="pd-mb-3" variant="error">
            <FormattedMessage
              id="product.unavailable.quoteModal.submitError"
              defaultMessage="Something went wrong while submitting your quote."
            />
          </Alert>
          )}

          {status === STATUS.POSTED && (
            <>
              <Alert className="pd-mb-5" variant="success">
                <FormattedMessage
                  id="product.unavailable.quoteModal.submitSuccess"
                  defaultMessage="Thanks for submitting your quote request. We will contact you soon."
                />
              </Alert>

              <Button as="a" href={`${channel.siteUrl}${homePath}`} iconBefore="home">
                <FormattedMessage
                  id="product.unavailable.quoteModal.submitSuccess.homeButton"
                  defaultMessage="Back to the homepage"
                />
              </Button>
            </>
          )}

          {status !== STATUS.POSTED && (
          <form onSubmit={handleSubmit} method="POST">
            <fieldset disabled={isSubmitting}>
              <InventoryCheckQuoteForm
                formik={quoteFormFormik}
                product={product}
                shouldUserAddContactInfo={shouldUserAddContactInfo}
                contentfulId={contentfulId}
              />
              <div className="pd-mb-5 pd-mt-7">
                <Button type="submit">
                  <FormattedMessage
                    id="product.unavailable.quoteModal.submitButton"
                    defaultMessage="Submit quote request"
                  />
                </Button>
              </div>
            </fieldset>
          </form>
          )}
        </Modal.Content>
      </Modal>
    </>
  );
};

export default InventoryCheckQuoteButton;
